import { FC, useEffect, useState } from "react";
import { Box, Popover, Typography } from "@mui/material";
import IconButton from "@mui/material/IconButton";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import MonitorOutlinedIcon from "@mui/icons-material/MonitorOutlined";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import NotificationsNoneOutlinedIcon from "@mui/icons-material/NotificationsNoneOutlined";
import CalculateOutlinedIcon from "@mui/icons-material/CalculateOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";

import { useAppDispatch, useAppSelector } from "../../../hooks";
import { Colors } from "../../../configs";
import { useTranslation } from "../../../util/translation-provider";
import { lockProfileAction, logoutAction } from "../../../redux/actions/authAction";
import DraftDrawer from "../../pos/draft-drawer/draftDrawer";
import DraggableCalculator from "../../pos/calculator/DraggableCalculator";
import ActionListModal from "../../atoms/action-list-modal/ActionListModal";
import UnfinishedOrdersModal from "../../pos/unfinished-orders-modal/UnfinishedOrdersModal";

const TopBar: FC = () => {
  const [draftDrawerOpen, setDraftDrawerOpen] = useState(false);
  const [calculatorOpen, setCalculatorOpen] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [actionsOpen, setActionsOpen] = useState(false);
  const [unfinishedOrdersOpen, setUnfinishedOrdersOpen] = useState(false);
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const { customer } = useAppSelector((state) => state.customer);
  const drafts = useAppSelector((state) => state.cart.drafts);
  const [now, setNow] = useState(new Date());
  const [calendarAnchor, setCalendarAnchor] = useState<HTMLElement | null>(null);
  const [calendarDate, setCalendarDate] = useState<Date>(new Date());
  const translations = useTranslation();

  const toggleDraftDrawer = () => setDraftDrawerOpen((prev) => !prev);
  const toggleCalculator = () => setCalculatorOpen((prev) => !prev);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  useEffect(() => {
    const interval = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(interval);
  }, []);

  const stationStatus = JSON.parse(
    localStorage.getItem("activeCashierSession") || "{}"
  )?.status;

  const cashierStation = JSON.parse(localStorage.getItem("cashier") || "{}")
    ?.cashierPoint?.name;

  const dateStr = now.toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
  const timeStr = now.toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
  });

  const Divider = () => (
    <Box
      sx={{
        width: "1px",
        height: "1rem",
        backgroundColor: "rgba(255,255,255,0.35)",
        flexShrink: 0,
      }}
    />
  );

  return (
    <Box
      sx={{
        backgroundColor: "primary.main",
        color: Colors.WHITE,
        px: "1.5rem",
        py: "0.75rem",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        width: "100%",
        boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
      }}
    >
      {/* Left: Avatar + Company Info */}
      <Box sx={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
        <Box
          sx={{
            width: "2.5rem",
            height: "2.5rem",
            borderRadius: "50%",
            backgroundColor: "rgba(255,255,255,0.2)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "1.2rem",
            fontWeight: 700,
            flexShrink: 0,
          }}
        >
          P
        </Box>
        <Box sx={{ lineHeight: 1.25 }}>
          <Typography
            sx={{ fontSize: "1.1rem", fontWeight: 600, color: Colors.WHITE, lineHeight: 1.3 }}
          >
            {translations.companyName}
          </Typography>
          <Typography
            sx={{ fontSize: "0.85rem", color: "rgba(255,255,255,0.75)", lineHeight: 1.2 }}
          >
            {translations.companyAddress}
          </Typography>
        </Box>
      </Box>

      {/* Right: Controls */}
      <Box sx={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
        {/* Phone */}
        <Box sx={{ display: "flex", alignItems: "center", gap: "0.3rem" }}>
          <PhoneOutlinedIcon sx={{ fontSize: "1.1rem", color: Colors.WHITE }} />
          <Typography sx={{ fontSize: "0.95rem", color: Colors.WHITE }}>
            {translations.companyPhone}
          </Typography>
        </Box>

        {/* Email */}
        <Box sx={{ display: "flex", alignItems: "center", gap: "0.3rem" }}>
          <EmailOutlinedIcon sx={{ fontSize: "1.1rem", color: Colors.WHITE }} />
          <Typography sx={{ fontSize: "0.95rem", color: Colors.WHITE }}>
            {translations.companyEmail}
          </Typography>
        </Box>

        <Divider />

        {/* User info (display only when logged in) */}
        {user && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "0.3rem",
            }}
          >
            <PersonOutlineOutlinedIcon sx={{ fontSize: "1.1rem", color: Colors.WHITE }} />
            <Typography sx={{ fontSize: "0.95rem", color: Colors.WHITE }}>
              {`${user.firstName} ${user.lastName}`}
            </Typography>
            <Box
              sx={{
                width: "0.45rem",
                height: "0.45rem",
                borderRadius: "50%",
                backgroundColor: "#4ADE80",
              }}
            />
          </Box>
        )}

        {/* Station */}
        <Box sx={{ display: "flex", alignItems: "center", gap: "0.3rem" }}>
          <MonitorOutlinedIcon sx={{ fontSize: "1.1rem", color: Colors.WHITE }} />
          <Typography sx={{ fontSize: "0.95rem", color: Colors.WHITE }}>
            {cashierStation ?? "POS"}
          </Typography>
        </Box>

        <Divider />

        {/* Date/Time pill */}
        <Box
          onClick={(e) => setCalendarAnchor(e.currentTarget)}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "0.35rem",
            px: "0.6rem",
            py: "0.25rem",
            borderRadius: "0.75rem",
            backgroundColor: calendarAnchor ? "rgba(255,255,255,0.22)" : "rgba(255,255,255,0.12)",
            cursor: "pointer",
            userSelect: "none",
            transition: "background-color 0.15s ease",
            "&:hover": { backgroundColor: "rgba(255,255,255,0.22)" },
          }}
        >
          <CalendarTodayOutlinedIcon sx={{ fontSize: "1rem", color: "rgba(255,255,255,0.8)" }} />
          <Typography sx={{ fontSize: "0.9rem", fontWeight: 500, color: Colors.WHITE }}>
            {dateStr}
          </Typography>
          <Typography sx={{ fontSize: "0.9rem", color: "rgba(255,255,255,0.55)" }}>•</Typography>
          <Typography sx={{ fontSize: "0.9rem", fontWeight: 700, color: Colors.WHITE }}>
            {timeStr}
          </Typography>
        </Box>

        <Popover
          open={Boolean(calendarAnchor)}
          anchorEl={calendarAnchor}
          onClose={() => setCalendarAnchor(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          transformOrigin={{ vertical: "top", horizontal: "center" }}
          slotProps={{
            paper: {
              sx: {
                mt: "0.5rem",
                borderRadius: "1rem",
                boxShadow: "0 8px 32px rgba(0,0,0,0.4)",
                overflow: "hidden",
                border: "1px solid rgba(255,255,255,0.08)",
                backgroundColor: "#2D2D2D",
                color: "#FFFFFF",
              },
            },
          }}
        >
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DateCalendar
              value={calendarDate}
              onChange={(date) => date && setCalendarDate(date)}
              sx={{
                width: "300px",
                height: "auto",
                "& .MuiPickersDay-root": {
                  color: "#E0E0E0",
                },
                "& .MuiPickersDay-root.Mui-selected": {
                  backgroundColor: Colors.PRIMARY,
                  color: "#FFFFFF",
                  "&:hover": { backgroundColor: "#016B42" },
                  "&:focus": { backgroundColor: Colors.PRIMARY },
                },
                "& .MuiPickersDay-root:not(.Mui-selected):hover": {
                  backgroundColor: "rgba(1,135,82,0.25)",
                },
                "& .MuiPickersDay-today:not(.Mui-selected)": {
                  borderColor: Colors.PRIMARY,
                },
                "& .MuiPickersArrowSwitcher-button": {
                  color: "#E0E0E0",
                },
                "& .MuiPickersCalendarHeader-switchViewButton": {
                  color: "#E0E0E0",
                },
                "& .MuiPickersCalendarHeader-label": {
                  color: "#FFFFFF",
                },
                "& .MuiDayCalendar-weekDayLabel": {
                  color: "rgba(255,255,255,0.5)",
                },
                "& .MuiDayCalendar-root": {
                  width: "300px",
                },
                "& .MuiDayCalendar-weekContainer": {
                  justifyContent: "space-around",
                },
                "& .MuiDayCalendar-header": {
                  justifyContent: "space-around",
                },
              }}
            />
          </LocalizationProvider>
        </Popover>

        <Divider />

        {/* Draft (hidden until logged in) */}
        {user && (
          <>
            <IconButton
              sx={{
                color: Colors.WHITE,
                p: "0.3rem",
                "&:hover": { backgroundColor: "rgba(255,255,255,0.1)" },
              }}
              disabled={stationStatus !== "open"}
              onClick={toggleDraftDrawer}
              title="Drafts"
            >
              <Box sx={{ position: "relative", display: "inline-flex" }}>
                <ReceiptLongOutlinedIcon sx={{ fontSize: "1.3rem" }} />
                <Box
                  component="span"
                  sx={{
                    position: "absolute",
                    top: "-0.45rem",
                    right: "-0.55rem",
                    backgroundColor: "#EAB308",
                    color: "#fff",
                    fontSize: "0.5rem",
                    fontWeight: 700,
                    lineHeight: 1,
                    px: "0.25rem",
                    py: "0.1rem",
                    borderRadius: "0.25rem",
                    pointerEvents: "none",
                    whiteSpace: "nowrap",
                  }}
                >
                  Draft
                </Box>
              </Box>
            </IconButton>
            <DraftDrawer
              isOpen={draftDrawerOpen}
              toggleDrawer={toggleDraftDrawer}
              currentCustomerId={customer?.id}
            />
          </>
        )}

        {/* Notification bell (hidden until logged in) */}
        {user && (
          <Box sx={{ position: "relative" }}>
            <IconButton
              sx={{
                color: Colors.WHITE,
                p: "0.3rem",
                "&:hover": { backgroundColor: "rgba(255,255,255,0.1)" },
              }}
              title="Notifications"
            >
              <NotificationsNoneOutlinedIcon sx={{ fontSize: "1.3rem" }} />
            </IconButton>
            <Box
              sx={{
                position: "absolute",
                top: "0.25rem",
                right: "0.25rem",
                width: "0.4rem",
                height: "0.4rem",
                borderRadius: "50%",
                backgroundColor: "#EF4444",
                pointerEvents: "none",
              }}
            />
          </Box>
        )}

        {/* Calculator */}
        <IconButton
          onClick={toggleCalculator}
          sx={{
            color: Colors.WHITE,
            p: "0.3rem",
            "&:hover": { backgroundColor: "rgba(255,255,255,0.1)" },
          }}
          title="Calculator"
        >
          <CalculateOutlinedIcon sx={{ fontSize: "1.3rem" }} />
        </IconButton>

        <Divider />

        {/* Lock button — opens ActionListModal (same as lobby) */}
        <IconButton
          onClick={() => setActionsOpen(true)}
          sx={{
            color: Colors.WHITE,
            p: "0.35rem",
            borderRadius: "0.5rem",
            backgroundColor: "#EF4444",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            "&:hover": { backgroundColor: "#DC2626" },
          }}
          title="Lock"
        >
          <LockOutlinedIcon sx={{ fontSize: "1.3rem" }} />
        </IconButton>

        <Divider />

        {/* Fullscreen button */}
        <IconButton
          onClick={toggleFullscreen}
          sx={{
            color: Colors.WHITE,
            p: "0.3rem",
            borderRadius: "0.5rem",
            backgroundColor: "rgba(255,255,255,0.1)",
            "&:hover": { backgroundColor: "rgba(255,255,255,0.2)" },
          }}
          title={isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"}
        >
          {isFullscreen ? (
            <FullscreenExitIcon sx={{ fontSize: "1.3rem" }} />
          ) : (
            <FullscreenIcon sx={{ fontSize: "1.3rem" }} />
          )}
        </IconButton>
      </Box>

      <ActionListModal
        open={actionsOpen}
        onClose={() => setActionsOpen(false)}
        onLock={() => {
          setActionsOpen(false);
          dispatch(lockProfileAction());
        }}
        onLogout={() => {
          setActionsOpen(false);
          if (drafts.length > 0) {
            setUnfinishedOrdersOpen(true);
          } else {
            dispatch(logoutAction());
          }
        }}
      />

      <UnfinishedOrdersModal
        open={unfinishedOrdersOpen}
        orders={drafts.filter(Boolean).map((d) => ({
          id: String(d.draftId),
          customerName: d.customer
            ? `${d.customer.firstName} ${d.customer.lastName}`
            : "Unknown",
          pieces: d.totalPieces,
          amount: d.totalAmount != null ? `$${d.totalAmount.toFixed(2)}` : undefined,
          lastEdited: new Date(d.createdAt).toLocaleTimeString(undefined, {
            hour: "2-digit",
            minute: "2-digit",
          }),
        }))}
        onClose={() => setUnfinishedOrdersOpen(false)}
      />

      <DraggableCalculator
        isOpen={calculatorOpen}
        onClose={() => setCalculatorOpen(false)}
        onResult={(result) => {
          console.log("Calculator result:", result);
        }}
      />
    </Box>
  );
};

export { TopBar };
